import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Col, Pagination, Row } from '@openedx/paragon';
import classNames from 'classnames';
import { AlertBanner, AlertTypes } from 'components/ui/atoms';
import { PageLoader } from 'components/ui/atoms/loader/page-loader';
import { DEFAULT_PAGE_SIZE } from 'helpers/constants';
import { useDiscountedProducts } from 'frontastic/hooks/useProducts';
import { getAlertMessage } from '../checkout/components/alert.messages';
import {
  DiscountFooter,
  DiscountProductCard,
  DiscountProductCardSkeleton,
} from './components';
import { messages } from './messages';
import { RedeemDiscountProps } from './types';

export const RedeemDiscount = ({ discountCode }: RedeemDiscountProps) => {
  const { formatMessage } = useIntl();
  const [currentPage, setCurrentPage] = useState(1);

  const {
    data: productResponse,
    isLoading,
    isError: isGeneralError,
  } = useDiscountedProducts(discountCode);
  const { products, error, isError: isApiError } = productResponse;

  const totalCount = products.length;
  const isError = isGeneralError || isApiError;
  const pageCount = Math.ceil(products.length / DEFAULT_PAGE_SIZE);

  const handlePageSelect = (page: number) => {
    setCurrentPage(page);
  };

  const renderedCourses = useMemo(() => {
    const pageOffset = (currentPage - 1) * DEFAULT_PAGE_SIZE;
    return products
      .slice(pageOffset, pageOffset + DEFAULT_PAGE_SIZE)
      .map((product) => (
        <Col key={product.courseRunKey} className="col-xl-6 col-12 content-div">
          <DiscountProductCard
            product={product}
            discountCode={discountCode}
            isLoading={isLoading}
          />
        </Col>
      ));
  }, [products, currentPage]);

  const renderCardSkeleton = () =>
    Array.from({ length: 6 }).map((key) => (
      <Col
        key={`skeleton-${key as number}`}
        className="col-xl-6 col-12 content-div"
      >
        <DiscountProductCardSkeleton />
      </Col>
    ));

  const renderPagination = () => (
    <Col className="col-12 d-flex justify-content-end content-div">
      <Pagination
        paginationLabel="pagination navigation"
        pageCount={pageCount}
        currentPage={currentPage}
        onPageSelect={(page: number) => handlePageSelect(page)}
      />
    </Col>
  );

  if (isLoading && !products.length) {
    return (
      <PageLoader
        loaderText={formatMessage(messages['redeem.discount.page.loader'])}
      />
    );
  }

  const renderContent = () => (
    <Row className="row justify-content-md-center">
      <Col className="col-xl-12 col-lg-9 col-12">
        <h1>{formatMessage(messages['redeem.discount.page.heading'])}</h1>
        <p>{formatMessage(messages['redeem.discount.page.subheading'])}</p>
        <hr />
        <Row className="my-4">
          {isLoading ? renderCardSkeleton() : renderedCourses}
          {totalCount > DEFAULT_PAGE_SIZE && renderPagination()}
        </Row>
      </Col>
      <DiscountFooter />
    </Row>
  );

  const renderError = () => (
    <AlertBanner
      variant={AlertTypes.DANGER}
      onClose={() => {
        window.location.replace(
          process.env.NEXT_PUBLIC_EDX_LEARNER_DASHBOARD_URL!,
        );
      }}
    >
      {getAlertMessage(error, discountCode)}
    </AlertBanner>
  );

  return (
    <div
      className={classNames(
        'container-fluid',
        isError ? 'py-5' : 'container-mw-lg',
      )}
    >
      {isError ? renderError() : renderContent()}
    </div>
  );
};
